import axiosInstance from '../config/axios.js';

const API_BASE = '/company-subscriptions';
const PAYMENT_BASE = '/subscription-payments';

const companySubscriptionService = {
  // ==================== Subscription Operations ====================
  
  // Get all subscriptions with optional filters
  getAll: async (params = {}) => {
    const response = await axiosInstance.get(API_BASE, { params });
    return response.data;
  },

  // Get subscription by ID
  getById: async (subscriptionId) => {
    const response = await axiosInstance.get(`${API_BASE}/${subscriptionId}`);
    return response.data;
  },

  // Get subscriptions by company ID
  getByCompany: async (companyId, includeInactive = false) => {
    const response = await axiosInstance.get(`${API_BASE}/company/${companyId}`, {
      params: { include_inactive: includeInactive }
    });
    return response.data;
  },

  // Create new subscription
  create: async (subscriptionData) => {
    const response = await axiosInstance.post(API_BASE, subscriptionData);
    return response.data;
  },

  // Update subscription
  update: async (subscriptionId, subscriptionData) => {
    const response = await axiosInstance.put(`${API_BASE}/${subscriptionId}`, subscriptionData);
    return response.data;
  },

  // Delete subscription (soft delete)
  delete: async (subscriptionId) => {
    const response = await axiosInstance.delete(`${API_BASE}/${subscriptionId}`);
    return response.data;
  },

  // Renew subscription
  renew: async (subscriptionId, data = {}) => {
    const response = await axiosInstance.post(`${API_BASE}/${subscriptionId}/renew`, data);
    return response.data;
  },

  // Activate subscription
  activate: async (subscriptionId) => {
    const response = await axiosInstance.post(`${API_BASE}/${subscriptionId}/activate`);
    return response.data;
  },

  // Get active subscriptions
  getActiveSubscriptions: async (companyId = null) => {
    const params = companyId ? { company_id: companyId } : {};
    const response = await axiosInstance.get(`${API_BASE}/active`, { params });
    return response.data;
  },

  // Get expiring subscriptions
  getExpiringSubscriptions: async (days = 30) => {
    const response = await axiosInstance.get(`${API_BASE}/expiring`, {
      params: { days }
    });
    return response.data;
  },

  // ==================== Payment Operations ====================

  // Get all payments for a subscription
  getPaymentsBySubscription: async (subscriptionId) => {
    const response = await axiosInstance.get(`${PAYMENT_BASE}/subscription/${subscriptionId}`);
    return response.data;
  },

  // Get payment by ID
  getPaymentById: async (paymentId) => {
    const response = await axiosInstance.get(`${PAYMENT_BASE}/${paymentId}`);
    return response.data;
  },

  // Get payments by company
  getPaymentsByCompany: async (companyId, status = null) => {
    const params = status ? { status } : {};
    const response = await axiosInstance.get(`${PAYMENT_BASE}/company/${companyId}`, { params });
    return response.data;
  },

  // Create payment
  createPayment: async (paymentData, receiptFile = null) => {
    const formData = new FormData();
    
    // Append payment data
    Object.keys(paymentData).forEach(key => {
      if (paymentData[key] !== null && paymentData[key] !== undefined) {
        formData.append(key, paymentData[key]);
      }
    });

    // Append receipt if provided
    if (receiptFile) {
      formData.append('receipt', receiptFile);
    }

    const response = await axiosInstance.post(PAYMENT_BASE, formData, {
      headers: {
        'Content-Type': undefined,
      },
    });
    return response.data;
  },

  // Update payment
  updatePayment: async (paymentId, paymentData, receiptFile = null) => {
    const formData = new FormData();
    
    // Append payment data
    Object.keys(paymentData).forEach(key => {
      if (paymentData[key] !== null && paymentData[key] !== undefined) {
        formData.append(key, paymentData[key]);
      }
    });

    // Append receipt if provided
    if (receiptFile) {
      formData.append('receipt', receiptFile);
    }

    const response = await axiosInstance.put(`${PAYMENT_BASE}/${paymentId}`, formData, {
      headers: {
        'Content-Type': undefined,
      },
    });
    return response.data;
  },

  // Delete payment
  deletePayment: async (paymentId) => {
    const response = await axiosInstance.delete(`${PAYMENT_BASE}/${paymentId}`);
    return response.data;
  },

  // Mark payment as completed
  completePayment: async (paymentId, verifiedBy = null) => {
    const response = await axiosInstance.post(`${PAYMENT_BASE}/${paymentId}/complete`, {
      verified_by: verifiedBy
    });
    return response.data;
  },

  // Mark payment as failed
  failPayment: async (paymentId, remarks = null) => {
    const response = await axiosInstance.post(`${PAYMENT_BASE}/${paymentId}/fail`, {
      remarks
    });
    return response.data;
  },

  // Get payment receipt
  getReceipt: (paymentId) => {
    return `${axiosInstance.defaults.baseURL}${PAYMENT_BASE}/${paymentId}/receipt`;
  },

  // Download payment receipt
  downloadReceipt: async (paymentId) => {
    const response = await axiosInstance.get(`${PAYMENT_BASE}/${paymentId}/receipt`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Get pending payments
  getPendingPayments: async (companyId = null) => {
    const params = companyId ? { company_id: companyId } : {};
    const response = await axiosInstance.get(`${PAYMENT_BASE}/pending`, { params });
    return response.data;
  },

  // Get completed payments
  getCompletedPayments: async (companyId = null, startDate = null, endDate = null) => {
    const params = {};
    if (companyId) params.company_id = companyId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    const response = await axiosInstance.get(`${PAYMENT_BASE}/completed`, { params });
    return response.data;
  },

  // Get payment summary
  getPaymentSummary: async (companyId = null, startDate = null, endDate = null) => {
    const params = {};
    if (companyId) params.company_id = companyId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    const response = await axiosInstance.get(`${PAYMENT_BASE}/summary`, { params });
    return response.data;
  },

  // ==================== Utility Functions ====================

  // Calculate subscription end date based on period type
  calculateEndDate: (startDate, periodType, customYears = null) => {
    const start = new Date(startDate);
    let endDate = new Date(start);

    switch (periodType) {
      case 'seven_days':
        endDate.setDate(endDate.getDate() + 7);
        break;
      case 'one_month':
        endDate.setMonth(endDate.getMonth() + 1);
        break;
      case 'three_months':
        endDate.setMonth(endDate.getMonth() + 3);
        break;
      case 'six_months':
        endDate.setMonth(endDate.getMonth() + 6);
        break;
      case 'twelve_months':
        endDate.setFullYear(endDate.getFullYear() + 1);
        break;
      case 'custom_years':
        if (customYears && customYears > 0) {
          endDate.setFullYear(endDate.getFullYear() + customYears);
        }
        break;
      default:
        break;
    }

    return endDate.toISOString().split('T')[0];
  },

  // Get period type display name
  getPeriodDisplayName: (periodType, customYears = null) => {
    const periodNames = {
      'seven_days': '7 Days',
      'one_month': '1 Month',
      'three_months': '3 Months',
      'six_months': '6 Months',
      'twelve_months': '12 Months',
      'custom_years': customYears ? `${customYears} Year${customYears > 1 ? 's' : ''}` : 'Custom Years'
    };
    return periodNames[periodType] || periodType;
  },

  // Get payment method display name
  getPaymentMethodDisplayName: (method) => {
    const methodNames = {
      'cash': 'Cash',
      'bank_transfer': 'Bank Transfer',
      'cheque': 'Cheque',
      'upi': 'UPI',
      'credit_card': 'Credit Card',
      'debit_card': 'Debit Card',
      'net_banking': 'Net Banking',
      'other': 'Other'
    };
    return methodNames[method] || method;
  },

  // Get payment status display name
  getPaymentStatusDisplayName: (status) => {
    const statusNames = {
      'pending': 'Pending',
      'completed': 'Completed',
      'failed': 'Failed',
      'refunded': 'Refunded',
      'cancelled': 'Cancelled'
    };
    return statusNames[status] || status;
  }
};

export default companySubscriptionService;